'use client';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@components/ui/dialog';
import { Input } from '@components/ui/input-2024';
import { SUBSCRIPTION_PLAN } from '@/_data/constants';
import { useSession } from '@hooks/index';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import * as React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Loader2 } from 'lucide-react';
import { api } from '@/trpc/react';
import { getProductsByType, slugify } from '@acme/lib';
import { PricingBundleSelector } from '@components/Pricing/PricingBundleSelector';

interface PricingProps {
  coupon?: string;
  discountPercent?: number;
  expiresAt?: number;
  className?: string;
}

export default function Pricing({ coupon, discountPercent, expiresAt }: PricingProps) {
  const tiers = getProductsByType('initial-purchase')!;
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isLoggedIn } = useSession();
  const subscriptionQuery = api.user.getSubscriptionStatus.useQuery(undefined, {
    enabled: isLoggedIn
  });

  const [isGettingSession, setIsGettingSession] = React.useState(false);
  const [errorMessage] = React.useState<string | null>(null);
  const [isGiftDialogOpen, setIsGiftDialogOpen] = React.useState(false);
  const [friendsEmail, setFriendsEmail] = React.useState('');
  const [tier] = React.useState<(typeof tiers)[0] | null>(null);

  const calculatePrice = (price: number) => {
    const priceNumber = price;
    const discountedPrice = priceNumber * (1 - (discountPercent || 0) / 100);
    return {
      original: `$${priceNumber}`,
      discounted: `$${discountedPrice.toFixed(2)}`
    };
  };

  const tiersWithDiscount = tiers?.map(tier => ({
    ...tier,
    prices: calculatePrice(tier.price!)
  }));

  const isGift = searchParams?.get('gift') === 'true';

  const handleSubmitGift: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    if (!tier) return;
    setIsGettingSession(true);

    const params = new URLSearchParams({
      quantity: '1',
      returnUrl: '/dashboard',
      giftEmail: friendsEmail
    });

    router.push(`/payment/initial-purchase/checkout/${slugify(tier.productName)}?${params.toString()}`);

    setIsGettingSession(false);
    setIsGiftDialogOpen(false);
  };

  const formatExpiryDate = (timestamp?: number) => {
    if (!timestamp) return null;
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  return (
    <div className="" id="pricing">
      <div className="mx-auto pb-24 text-left md:text-center w-full flex flex-col relative">
        {coupon && (
          <Alert className="mb-8 max-w-2xl mx-auto relative">
            <div className="flex flex-col items-center">
              <AlertTitle className="text-lg font-bold">Exclusive {discountPercent}% Discount Applied!</AlertTitle>
              <AlertDescription className="text-sm">
                Your special discount has been automatically applied to all packages below.
                {expiresAt && (
                  <span className="block mt-2 font-medium">
                    ⏰ Hurry! This offer expires on {formatExpiryDate(expiresAt)}
                  </span>
                )}
              </AlertDescription>
            </div>
          </Alert>
        )}

        <div className="">
          <div className="mb-6">
            <h2 className="text-base font-semibold leading-7 text-foreground">Pricing</h2>
            <p className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl mb-10 w-full text-balance">
              {isGift ? 'Get a bundle for a friend' : 'Pro Headshots. No Photographer.'}
            </p>
          </div>
          <div className="mx-auto grid max-w-md gap-8 lg:max-w-7xl  text-left ">
            <PricingBundleSelector
              products={tiersWithDiscount}
              discountCode={coupon}
              discountPercentage={discountPercent}
              variant="direct"
              redirectToTrial={!coupon && subscriptionQuery?.data?.plan !== SUBSCRIPTION_PLAN.picstudio_drive}
              buttonText={isGift ? 'Gift portraits' : undefined}
              loginRedirectUrl="/pricing"
            />
          </div>

          <Dialog
            open={isGiftDialogOpen}
            onOpenChange={isOpen => {
              setIsGiftDialogOpen(isOpen);
            }}
          >
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Gift a bundle to a friend!</DialogTitle>
                <DialogDescription>Enter their email and we will send them the goods.</DialogDescription>
                {errorMessage && (
                  <DialogDescription>
                    <div className="text-red-500 text-sm">{errorMessage}</div>
                  </DialogDescription>
                )}
              </DialogHeader>
              <form onSubmit={handleSubmitGift}>
                <Input
                  value={friendsEmail}
                  onChange={e => setFriendsEmail(e.target.value)}
                  id="friendsEmail"
                  label="Friends email"
                  type="email"
                />
                <DialogFooter>
                  <Button disabled={isGettingSession} type="submit" className="mt-4">
                    Continue {isGettingSession && <Loader2 className="animate-spin" />}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
